/**
 * Frame price calculator
 * Estimates framing cost based on artwork dimensions, frame, mat, and glass selection.
 */

import { useMemo } from 'react';
import { cn } from '@frameit/ui';
import { useSelectedArtwork } from '../../stores/projectStore';
import { FRAMES } from '../../data/frames';
import { MATS } from '../../data/mats';
import {
  FRAME_MATERIAL_PRICES,
  FRAME_PROFILE_SURCHARGE,
  FRAME_TEXTURE_SURCHARGE,
  PREMIUM_MULTIPLIER,
  MAT_BASE_PRICE,
  MAT_PREMIUM_PRICE,
  MAT_TEXTURE_SURCHARGE,
  GLASS_PRICES,
  LABOUR_COST,
  MINIMUM_ORDER,
} from '../../data/pricing';

interface PriceBreakdown {
  framePrice: number;
  matPrice: number;
  glassPrice: number;
  labourPrice: number;
  totalPrice: number;
  frameName: string;
  matName: string | null;
  artworkSizeCm: { width: number; height: number } | null;
}

/** Calculate estimated framing price */
function calculatePrice(
  artworkWidthPx: number | undefined,
  artworkHeightPx: number | undefined,
  frameTemplateId: string,
  frameWidthMm: number,
  matTemplateId: string | undefined,
  matWidthRatio: number,
  glassType: string,
): PriceBreakdown {
  const frameData = FRAMES.find((f) => f.id === frameTemplateId);
  const matData = matTemplateId ? MATS.find((m) => m.id === matTemplateId) : null;

  const frameName = frameData?.name ?? 'Unknown';
  const matName = matData?.name ?? null;

  // Artwork dimensions at 300 DPI
  const DPI = 300;
  const MM_PER_INCH = 25.4;

  let artworkSizeCm: { width: number; height: number } | null = null;
  let artworkWidthMm = 300; // default ~30cm
  let artworkHeightMm = 400; // default ~40cm

  if (artworkWidthPx && artworkHeightPx) {
    artworkWidthMm = (artworkWidthPx * MM_PER_INCH) / DPI;
    artworkHeightMm = (artworkHeightPx * MM_PER_INCH) / DPI;
    artworkSizeCm = {
      width: Math.round((artworkWidthMm / 10) * 10) / 10,
      height: Math.round((artworkHeightMm / 10) * 10) / 10,
    };
  }

  // Mat width in mm
  const shortestSide = Math.min(artworkWidthMm, artworkHeightMm);
  const matWidthMm = matData ? matWidthRatio * shortestSide : 0;

  // Overall framed size in mm
  const totalWidthMm = artworkWidthMm + matWidthMm * 2 + frameWidthMm * 2;
  const totalHeightMm = artworkHeightMm + matWidthMm * 2 + frameWidthMm * 2;

  // Frame price: perimeter in meters × material price + surcharges
  const perimeterM = ((totalWidthMm + totalHeightMm) * 2) / 1000;

  const materialPrice = FRAME_MATERIAL_PRICES[frameData?.material ?? 'wood'] ?? 18;
  const profileSurcharge = FRAME_PROFILE_SURCHARGE[frameData?.profile ?? 'flat'] ?? 0;
  const textureSurcharge = FRAME_TEXTURE_SURCHARGE[frameData?.texture ?? 'smooth'] ?? 0;

  let framePrice = perimeterM * (materialPrice + profileSurcharge + textureSurcharge);

  // Width surcharge: wider frames cost more
  const widthFactor = Math.max(1, frameWidthMm / 25);
  framePrice *= widthFactor;

  // Premium surcharge
  if (frameData?.isPremium) {
    framePrice *= PREMIUM_MULTIPLIER;
  }

  // Mat price: base + texture + premium
  let matPrice = 0;
  if (matData) {
    matPrice = matData.isPremium ? MAT_PREMIUM_PRICE : MAT_BASE_PRICE;
    matPrice += MAT_TEXTURE_SURCHARGE[matData.texture ?? 'smooth'] ?? 0;

    // Size factor: larger mats cost more
    const matAreaFactor = Math.max(1, (totalWidthMm * totalHeightMm) / (500 * 700));
    matPrice *= matAreaFactor;
  }

  // Glass price (scaled by size)
  const glassBasePrice = GLASS_PRICES[glassType]?.price ?? GLASS_PRICES.regular.price;
  const glassAreaFactor = Math.max(1, (totalWidthMm * totalHeightMm) / (500 * 700));
  const glassPrice = glassBasePrice * glassAreaFactor;

  // Labour
  const labourPrice = LABOUR_COST;

  // Total
  const rawTotal = framePrice + matPrice + glassPrice + labourPrice;
  const totalPrice = Math.max(MINIMUM_ORDER, rawTotal);

  return {
    framePrice: Math.round(framePrice * 100) / 100,
    matPrice: Math.round(matPrice * 100) / 100,
    glassPrice: Math.round(glassPrice * 100) / 100,
    labourPrice,
    totalPrice: Math.round(totalPrice * 100) / 100,
    frameName,
    matName,
    artworkSizeCm,
  };
}

export interface PriceCalculatorProps {
  /** Artwork pixel dimensions (from loaded image) */
  artworkDimensions?: { width: number; height: number } | null;
  className?: string;
}

/** Price calculator component — shows estimated framing cost */
export function PriceCalculator({
  artworkDimensions,
  className,
}: PriceCalculatorProps): React.JSX.Element | null {
  const artwork = useSelectedArtwork();

  const price = useMemo(() => {
    if (!artwork) return null;

    return calculatePrice(
      artworkDimensions?.width,
      artworkDimensions?.height,
      artwork.frame.templateId,
      artwork.frame.width ?? 25,
      artwork.mat?.templateId,
      artwork.mat?.width ?? 0.1,
      'regular',
    );
  }, [artwork, artworkDimensions]);

  if (!artwork || !price) return null;

  return (
    <div className={cn('space-y-2', className)}>
      <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
        Estimated Cost
      </h4>

      <div className="space-y-1 text-sm">
        {/* Frame */}
        <div className="flex justify-between">
          <span className="text-muted-foreground">Frame ({price.frameName})</span>
          <span>{price.framePrice.toFixed(2)} &euro;</span>
        </div>

        {/* Mat */}
        {price.matName && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Mat ({price.matName})</span>
            <span>{price.matPrice.toFixed(2)} &euro;</span>
          </div>
        )}

        {/* Glass */}
        <div className="flex justify-between">
          <span className="text-muted-foreground">Glass (Regular)</span>
          <span>{price.glassPrice.toFixed(2)} &euro;</span>
        </div>

        {/* Labour */}
        <div className="flex justify-between">
          <span className="text-muted-foreground">Labour</span>
          <span>{price.labourPrice.toFixed(2)} &euro;</span>
        </div>

        {/* Total */}
        <div className="flex justify-between pt-1.5 border-t font-medium">
          <span>Total</span>
          <span>{price.totalPrice.toFixed(2)} &euro;</span>
        </div>

        {/* Size note */}
        {price.artworkSizeCm && (
          <p className="text-[10px] text-muted-foreground pt-1">
            Based on {price.artworkSizeCm.width} &times; {price.artworkSizeCm.height} cm artwork at
            300 DPI. Prices are estimates only.
          </p>
        )}
      </div>
    </div>
  );
}
